import React, { useEffect, useState } from 'react'
import { utility } from '../../framework/utility/utilityProvider';
import { COLUMNSTYPE, ENTITYNAME, LOCALSTORAGE_KEY, LOGEVENT, MODULE, PREFERENCE_ENTITYNAME, TOAST_TYPES } from '../../framework/constant/constant';
import * as API from '../../framework/API/api';
import BossDialog from '../../components/BossDialog';
import { EditPageHeader } from '../../components/EditPageHeader';
import { ConfirmAlert } from '../../ConfirmAlert';
import { useTranslation } from '../../locale/useTranslation';
import ToastNotification from '../../components/ToastNotification';
import ActionButton from '../../framework/forms/helpercomponents/buttons/ActionButton';

const SyncSeasonWithEpisodeForm = (props) => {

    const { dataItem, onClose } = props;
    const isSeries = Boolean(props.isSeries || (!props.seasonId && (props.seriesId || dataItem?.SeriesSynopsis || !dataItem?.Series_Id)));
    const lang = useTranslation();
    const [columns, setColumns] = useState([]);
    const [selectedColumnNames, setSelectedColumnNames] = useState([]);
    const [seasons, setSeasons] = useState(props.seasons || []);
    const [selectedSeasonIds, setSelectedSeasonIds] = useState([]);
    const [isEpisode, setIsEpisode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        loadData();
        if (isSeries) {
            loadSeasons();
        }
    }, [dataItem?._id]);

    const loadData = async () => {
        let currentUserId = utility.getValue(LOCALSTORAGE_KEY.user_id);
        let columns = await API.getFormData(PREFERENCE_ENTITYNAME.syncSeasonWithEpisode, currentUserId) ?? [];
        if (columns?.length === 0) {
            setColumns([]);
            return;
        }
        setColumns(columns.filter((column) => column.type !== COLUMNSTYPE.hidden));
    };

    const loadSeasons = async () => {
        if (props.seasons && props.seasons.length > 0) {
            setSeasons(props.seasons);
            return;
        }
        if (dataItem?._id) {
            let res = await API.getData(ENTITYNAME.Season, { query: ["Series_Id", "=", dataItem._id], sort: { SeasonNumber: 1 } });
            if (res.success && res.data) {
                setSeasons(res.data);
            }
        }
    };

    const onSeasonSelect = (seasonId) => {
        setSelectedSeasonIds((prev) => {
            if (prev.includes(seasonId)) {
                return prev.filter((id) => id !== seasonId);
            }
            return [...prev, seasonId];
        });
    };

    const onSelectAllSeasons = (e) => {
        setSelectedSeasonIds(e.target.checked ? seasons.map((s) => s._id) : []);
    };

    const onColumnSelect = (columnName) => {
        setSelectedColumnNames((prev) => {
            if (prev.includes(columnName)) {
                return prev.filter((name) => name !== columnName);
            }
            return [...prev, columnName];
        });
    };

    const onSelectAll = (e) => {
        setSelectedColumnNames(e.target.checked ? columns.map((column) => column.name) : []);
    };

    const handleSubmit = async (fieldNames = selectedColumnNames) => {
        if (isSeries && !selectedSeasonIds.length) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_at_least_one_season_error_message });
            return;
        }
        if (!fieldNames.length) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_at_least_one_column_error_message});
            return;
        }
        const confirmTitle = isSeries ? (lang.sync_series_metadata_label || lang.sync_label) : lang.sync_metadata_to_episodes_label;
        const confirmMessage = isSeries
            ? (isEpisode
                ? (`Are you sure you want to sync selected metadata to ${selectedSeasonIds.length} season(s) and their episodes?`)
                : (`Are you sure you want to sync selected metadata to ${selectedSeasonIds.length} season(s)?`))
            : lang.sync_metadata_to_episodes_confirmation_message;

        ConfirmAlert(async () => {
            setIsLoading(true);
            const selectedColumns = columns
                .filter((column) => fieldNames.includes(column.name))
                .map((column) => ({ ...column, isUpdate: true }));

            if (isSeries) {
                const payload = {
                    _id: dataItem?._id,
                    seriesId: dataItem?._id,
                    seasonIds: selectedSeasonIds,
                    fields: selectedColumns.map((column) => column.name),
                    isEpisode: isEpisode
                };

                let res = await API.syncSeriesMetadataWithSeasonAndEpisodes(payload);
                if (res.success) {
                    let logData = { event: LOGEVENT.SYNC_SEASON_METADATA, module: MODULE.MEDIA_EPISODE_SERIES, data: payload, message: res.message || lang.sync_series_metadata_label };
                    await API.SaveLogs(logData);
                    ToastNotification({ type: TOAST_TYPES.SUCCESS, title: lang.success_message_title, message: res.message || lang.metadata_synced_successfully_message });
                    onClose(true);
                } else {
                    ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: res.message || lang.failed_to_sync_metadata_error_message });
                }
            } else {
                const payload = {
                    _id: dataItem?._id,
                    fields: selectedColumns.map((column) => column.name)
                };

                let res = await API.syncSeasonMetadataWithEpisodesV2(payload);
                if (res.success) {
                    let logData = { event: LOGEVENT.SYNC_SEASON_METADATA, module: MODULE.MEDIA_EPISODE_SEASON, data: payload, message: res.message };
                    await API.SaveLogs(logData);
                    ToastNotification({ type: TOAST_TYPES.SUCCESS, title: lang.success_message_title, message: res.message });
                    onClose(true);
                } else {
                    ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: res.message });
                }
            }
            setIsLoading(false);
        },
            () => { },
            confirmTitle,
            confirmMessage
        );
    };

    const handleUpdateAll = () => {
        if (isSeries && !selectedSeasonIds.length) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_at_least_one_season_first_error_message });
            return;
        }
        const allColumnNames = columns.map((column) => column.name);
        setSelectedColumnNames(allColumnNames);
        handleSubmit(allColumnNames);
    };

    const selectedColumns = columns.filter((column) => selectedColumnNames.includes(column.name));
    const allSelected = columns.length > 0 && selectedColumnNames.length === columns.length;
    const allSeasonsSelected = seasons.length > 0 && selectedSeasonIds.length === seasons.length;

    return (
        <BossDialog
            width={isSeries ? "980px" : "760px"}
            title={isSeries ? (lang.sync_series_with_seasons_and_episodes_label) : (lang.sync_season_with_episodes_label)}
            onClose={() => onClose()}
        >
            <div className="d-flex align-items-center justify-content-between mb-2">
                <EditPageHeader
                    showTitle={false}
                    saveButtonTitle={lang.sync_label}
                    isLoading={isLoading}
                    onSubmit={handleSubmit}
                    onCancel={() => onClose()}
                />
                <div className="d-flex align-items-center gap-2 mt-1">
                    {isSeries && (
                        <label
                            className="d-flex align-items-center mb-0 mr-2"
                            style={{
                                cursor: "pointer",
                                fontWeight: 600,
                                fontSize: "13px",
                                backgroundColor: isEpisode ? "#e8f4fd" : "#f8f9fa",
                                border: isEpisode ? "1px solid #90caf9" : "1px solid #dee2e6",
                                padding: "6px 6px",
                                borderRadius: "4px",
                                width: "115px"
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={isEpisode}
                                onChange={(e) => setIsEpisode(e.target.checked)}
                                style={{ width: "16px", height: "16px", marginRight: "6px", cursor: "pointer" }}
                            />
                            <span>{lang.sync_episode_label}</span>
                        </label>
                    )}
                    <ActionButton
                        type="button"
                        btnColor="success"
                        name={lang.sync_all_label}
                        title={lang.sync_all_columns_tooltip}
                        disabled={!columns.length || (isSeries && !selectedSeasonIds.length)}
                        isLoading={isLoading}
                        onClick={handleUpdateAll}
                    />
                </div>
            </div>
            {isSeries ? (
                <div className="row" style={{ minHeight: "360px" }}>
                    <div className="col-4">
                        <div style={{ border: "1px solid #d7d7d7", height: "100%", backgroundColor: "#fff" }}>
                            <div className="d-flex align-items-center" style={{ padding: "8px 10px", borderBottom: "1px solid #e5e5e5", fontWeight: 600 }}>
                                <input
                                    type="checkbox"
                                    checked={allSeasonsSelected}
                                    onChange={onSelectAllSeasons}
                                    style={{ width: "15px", height: "15px" }}
                                    disabled={!seasons.length}
                                />
                                <span className="ml-2">{lang.seasons_label} ({selectedSeasonIds.length}/{seasons.length})</span>
                            </div>
                            <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                                {seasons.length === 0 && (
                                    <div className="text-muted p-3 text-center" style={{ fontSize: "13px" }}>
                                        {lang.no_seasons_found_message}
                                    </div>
                                )}
                                {seasons.map((seasonItem, idx) => {
                                    const seasonLabel = seasonItem.SeasonNumber
                                        ? (typeof seasonItem.SeasonNumber === 'string' && seasonItem.SeasonNumber.startsWith('Season')
                                            ? seasonItem.SeasonNumber
                                            : `${lang.season_label} ${seasonItem.SeasonNumber}`)
                                        : `${lang.season_label} ${idx + 1}`;
                                    const displayText = seasonItem.Title ? `${seasonLabel} - ${seasonItem.Title}` : seasonLabel;
                                    return (
                                        <label
                                            key={seasonItem._id || idx}
                                            className="d-flex align-items-center mb-0"
                                            style={{
                                                padding: "8px 10px",
                                                borderBottom: "1px solid #f1f1f1",
                                                cursor: "pointer",
                                                backgroundColor: selectedSeasonIds.includes(seasonItem._id) ? "#f0f8ff" : "transparent"
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedSeasonIds.includes(seasonItem._id)}
                                                onChange={() => onSeasonSelect(seasonItem._id)}
                                                style={{ width: "15px", height: "15px" }}
                                            />
                                            <span className="ml-2" style={{ flex: 1, fontSize: "13px" }} title={displayText}>
                                                {displayText}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="col-4">
                        <div style={{ border: "1px solid #d7d7d7", height: "100%", backgroundColor: "#fff" }}>
                            <div className="d-flex align-items-center" style={{ padding: "8px 10px", borderBottom: "1px solid #e5e5e5", fontWeight: 600 }}>
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={onSelectAll}
                                    disabled={!columns.length}
                                    style={{ width: "15px", height: "15px" }}
                                />
                                <span className="ml-2">{lang.update_button_tooltip}</span>
                            </div>
                            <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                                {columns.length === 0 && (
                                    <div className="text-muted p-3 text-center" style={{ fontSize: "13px" }}>
                                        {lang.no_columns_found_message}
                                    </div>
                                )}
                                {columns.map((column) => (
                                    <label
                                        key={column.name}
                                        className="d-flex align-items-center mb-0"
                                        style={{ padding: "8px 10px", borderBottom: "1px solid #f1f1f1", cursor: "pointer" }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedColumnNames.includes(column.name)}
                                            onChange={() => onColumnSelect(column.name)}
                                            style={{ width: "15px", height: "15px" }}
                                        />
                                        <span className="ml-2" style={{ flex: 1, fontSize: "13px" }}>{column.label ?? column.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="col-4">
                        <div style={{ border: "1px solid #d7d7d7", height: "100%", backgroundColor: "#fff" }}>
                            <div style={{ padding: "8px 10px", borderBottom: "1px solid #e5e5e5", fontWeight: 600 }}>
                                {lang.selected_columns_label || "Selected Columns"} ({selectedColumns.length})
                            </div>
                            <div style={{ maxHeight: "320px", overflowY: "auto", padding: "8px 10px" }}>
                                {selectedColumns.length === 0 && <div className="text-muted" style={{ fontSize: "13px" }}>{lang.no_columns_selected_message}</div>}
                                {selectedColumns.map((column) => (
                                    <div
                                        key={column.name}
                                        className="d-flex align-items-center"
                                        style={{ padding: "7px 0", borderBottom: "1px solid #f1f1f1", fontSize: "13px" }}
                                    >
                                        <span style={{ flex: 1 }}>{column.label ?? column.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="row" style={{ minHeight: "360px" }}>
                    <div className="col-6">
                        <div style={{ border: "1px solid #d7d7d7", height: "100%", backgroundColor: "#fff" }}>
                            <div className="d-flex align-items-center" style={{ padding: "8px 10px", borderBottom: "1px solid #e5e5e5", fontWeight: 600 }}>
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={onSelectAll}
                                    style={{ width: "15px", height: "15px" }}
                                />
                                <span className="ml-2">{lang.update_button_tooltip}</span>
                            </div>
                            <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                                {columns.map((column) => (
                                    <label
                                        key={column.name}
                                        className="d-flex align-items-center mb-0"
                                        style={{ padding: "8px 10px", borderBottom: "1px solid #f1f1f1", cursor: "pointer" }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedColumnNames.includes(column.name)}
                                            onChange={() => onColumnSelect(column.name)}
                                            style={{ width: "15px", height: "15px" }}
                                        />
                                        <span className="ml-2" style={{ flex: 1 }}>{column.label ?? column.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="col-6">
                        <div style={{ border: "1px solid #d7d7d7", height: "100%", backgroundColor: "#fff" }}>
                            <div style={{ padding: "8px 10px", borderBottom: "1px solid #e5e5e5", fontWeight: 600 }}>
                                {lang.selected_columns_label || "Selected Columns"}
                            </div>
                            <div style={{ maxHeight: "320px", overflowY: "auto", padding: "8px 10px" }}>
                                {selectedColumns.length === 0 && <div className="text-muted">{lang.no_columns_selected_message}</div>}
                                {selectedColumns.map((column) => (
                                    <div
                                        key={column.name}
                                        className="d-flex align-items-center"
                                        style={{ padding: "7px 0", borderBottom: "1px solid #f1f1f1" }}
                                    >
                                        <span style={{ flex: 1 }}>{column.label ?? column.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="mt-2" style={{ fontSize: "12px", color: "#b02a37", backgroundColor: "#f8d7da", padding: "6px 10px", borderRadius: "4px" }}>
                <span style={{ color: "red", fontWeight: "bold" }}>* </span>
                <strong>{lang.note_label || "Note:"}</strong> {isSeries ? (isEpisode ? (lang.sync_series_metadata_note_with_episodes ) : (lang.sync_series_metadata_note_without_episodes)) : (lang.sync_season_metadata_note)}
            </div>
        </BossDialog>
    )
}

export default SyncSeasonWithEpisodeForm
